import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Configuration from '../config';

const PAYPAL_SDK_ID = 'paypal-wallet-sdk';
const GOOGLE_PAY_SDK_ID = 'google-pay-sdk';
const APPLE_PAY_SDK_ID = 'apple-pay-sdk';
const DEFAULT_AMOUNT = 5;

const loadScript = (id, src) => {
  const existing = document.getElementById(id);
  if (existing) {
    return existing.getAttribute('data-loaded') === 'true'
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
        existing.addEventListener('load', resolve, {once: true});
        existing.addEventListener('error', reject, {once: true});
      });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const googlePayEnvironment = () => {
  const baseUrl = Configuration.baseUrl || '';
  return baseUrl.includes('localhost') || baseUrl.includes('staging') ? 'TEST' : 'PRODUCTION';
};

const WalletTopUpPaymentMethods = ({amount, token, isValidAmount, onInvalidAmount, onPending, onSuccess, onError}) => {
  const paypalRef = useRef(null);
  const googleRef = useRef(null);
  const appleRef = useRef(null);
  const paymentStartedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [processing, setProcessing] = useState(false);

  const amountNumber = useCallback(() => {
    if (!isValidAmount(amount)) {
      onInvalidAmount();
      throw new Error('invalid_amount');
    }

    return Number(amount || DEFAULT_AMOUNT);
  }, [amount, isValidAmount, onInvalidAmount]);

  const amountString = useCallback(() => amountNumber().toFixed(2), [amountNumber]);

  const createWalletOrder = useCallback(() => {
    paymentStartedRef.current = true;
    return fetch(`${Configuration.apiEndPoint}/paypal/wallet_orders`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      },
      mode: 'cors',
      body: JSON.stringify({amount: amountNumber()})
    }).then(res => res.json().then(json => ({ok: res.ok, json})))
      .then(({ok, json}) => {
        if (!ok || !json.id) {
          throw new Error(json.error || 'paypal_order_create_failed');
        }

        return json.id;
      });
  }, [amountNumber, token]);

  const captureWalletOrder = useCallback(orderID => {
    setProcessing(true);
    onPending();
    return fetch(`${Configuration.apiEndPoint}/paypal/wallet_orders/${orderID}/capture`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      },
      mode: 'cors'
    }).then(res => res.json().then(json => ({ok: res.ok, json})))
      .then(({ok, json}) => {
        if (!ok || !json.success) {
          throw new Error(json.error || 'paypal_order_capture_failed');
        }

        onSuccess();
        return json;
      })
      .catch(error => {
        onError(error);
        throw error;
      })
      .finally(() => setProcessing(false));
  }, [onError, onPending, onSuccess, token]);

  useEffect(() => {
    let cancelled = false;
    const merchantId = Configuration.paypalMerchantId
      ? `&merchant-id=${encodeURIComponent(Configuration.paypalMerchantId)}`
      : '';
    const paypalSrc = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(Configuration.paypalClientId)}${merchantId}&currency=EUR&intent=capture&components=buttons,googlepay,applepay`;

    loadScript(PAYPAL_SDK_ID, paypalSrc)
      .then(() => {
        if (!cancelled) {
          setReady(Boolean(window.paypal));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [onError]);

  useEffect(() => {
    const paypalContainer = paypalRef.current;
    if (!ready || !window.paypal || !paypalContainer) {
      return undefined;
    }

    paypalContainer.innerHTML = '';
    const buttons = window.paypal.Buttons({
      style: {
        height: 52,
        layout: 'vertical',
        shape: 'rect',
        tagline: false
      },
      createOrder: () => createWalletOrder(),
      onApprove: data => captureWalletOrder(data.orderID),
      onError: error => {
        if (paymentStartedRef.current) {
          onError(error);
        }
      },
      onShippingChange: (data, actions) => actions.resolve()
    });

    if (buttons.isEligible()) {
      buttons.render(paypalContainer);
    }

    return () => {
      if (buttons.close) {
        buttons.close();
      }
    };
  }, [captureWalletOrder, createWalletOrder, onError, ready]);

  useEffect(() => {
    const googleContainer = googleRef.current;
    if (!ready || !window.paypal || !window.paypal.Googlepay || !googleContainer) {
      return undefined;
    }

    let cancelled = false;
    googleContainer.innerHTML = '';

    loadScript(GOOGLE_PAY_SDK_ID, 'https://pay.google.com/gp/p/js/pay.js')
      .then(async () => {
        if (cancelled || !window.google || !window.google.payments) {
          return;
        }

        const googlePay = window.paypal.Googlepay();
        const googlePayConfig = await googlePay.config();
        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: googlePayEnvironment(),
          paymentDataCallbacks: {
            onPaymentAuthorized: async paymentData => {
              try {
                const orderID = await createWalletOrder();
                const confirmOrderResponse = await googlePay.confirmOrder({
                  orderId: orderID,
                  paymentMethodData: paymentData.paymentMethodData
                });

                if (confirmOrderResponse.status === 'PAYER_ACTION_REQUIRED' && googlePay.initiatePayerAction) {
                  await googlePay.initiatePayerAction({orderId: orderID});
                }

                if (confirmOrderResponse.status === 'APPROVED' || confirmOrderResponse.status === 'PAYER_ACTION_REQUIRED') {
                  await captureWalletOrder(orderID);
                  return {transactionState: 'SUCCESS'};
                }

                return {
                  transactionState: 'ERROR',
                  error: {intent: 'PAYMENT_AUTHORIZATION', message: 'TRANSACTION FAILED'}
                };
              } catch (error) {
                onError(error);
                return {
                  transactionState: 'ERROR',
                  error: {intent: 'PAYMENT_AUTHORIZATION', message: error.message}
                };
              }
            }
          }
        });
        const readyToPay = await paymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: googlePayConfig.allowedPaymentMethods
        });

        if (!readyToPay.result || cancelled) {
          return;
        }

        const button = paymentsClient.createButton({
          buttonColor: 'black',
          buttonLocale: 'it',
          buttonSizeMode: 'fill',
          buttonType: 'buy',
          onClick: async () => {
            try {
              const paymentDataRequest = {
                apiVersion: 2,
                apiVersionMinor: 0,
                allowedPaymentMethods: googlePayConfig.allowedPaymentMethods,
                merchantInfo: googlePayConfig.merchantInfo,
                transactionInfo: {
                  countryCode: 'IT',
                  currencyCode: 'EUR',
                  totalPriceStatus: 'FINAL',
                  totalPrice: amountString()
                },
                callbackIntents: ['PAYMENT_AUTHORIZATION']
              };
              await paymentsClient.loadPaymentData(paymentDataRequest);
            } catch (error) {
              if (error && error.statusCode !== 'CANCELED') {
                onError(error);
              }
            }
          }
        });
        googleContainer.appendChild(button);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      googleContainer.innerHTML = '';
    };
  }, [amountString, captureWalletOrder, createWalletOrder, onError, ready]);

  useEffect(() => {
    const appleContainer = appleRef.current;
    if (!ready || !window.paypal || !window.paypal.Applepay || !appleContainer) {
      return undefined;
    }

    let cancelled = false;
    appleContainer.innerHTML = '';

    loadScript(APPLE_PAY_SDK_ID, 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js')
      .then(async () => {
        if (cancelled || !window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
          return;
        }

        const applepay = window.paypal.Applepay();
        const applepayConfig = await applepay.config();
        if (!applepayConfig.isEligible || cancelled) {
          return;
        }

        const button = document.createElement('apple-pay-button');
        button.setAttribute('buttonstyle', 'black');
        button.setAttribute('type', 'buy');
        button.setAttribute('locale', 'it-IT');
        button.style.display = 'block';
        button.style.width = '100%';
        button.style.height = '52px';
        button.addEventListener('click', () => {
          let session;
          try {
            session = new window.ApplePaySession(4, {
              countryCode: applepayConfig.countryCode || 'IT',
              currencyCode: 'EUR',
              merchantCapabilities: applepayConfig.merchantCapabilities,
              supportedNetworks: applepayConfig.supportedNetworks,
              requiredBillingContactFields: ['postalAddress'],
              total: {
                label: 'Replayer',
                type: 'final',
                amount: amountString()
              }
            });
          } catch (error) {
            onError(error);
            return;
          }

          session.onvalidatemerchant = event => {
            applepay.validateMerchant({
              validationUrl: event.validationURL,
              displayName: 'Replayer'
            }).then(validateResult => {
              session.completeMerchantValidation(validateResult.merchantSession);
            }).catch(error => {
              onError(error);
              session.abort();
            });
          };

          session.onpaymentauthorized = async event => {
            try {
              const orderID = await createWalletOrder();
              await applepay.confirmOrder({
                orderId: orderID,
                token: event.payment.token,
                billingContact: event.payment.billingContact
              });
              await captureWalletOrder(orderID);
              session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
            } catch (error) {
              onError(error);
              session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            }
          };

          session.begin();
        });
        appleContainer.appendChild(button);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      appleContainer.innerHTML = '';
    };
  }, [amountString, captureWalletOrder, createWalletOrder, onError, ready]);

  return (
    <div className="wallet-topup-payment-methods">
      <div ref={paypalRef} className="wallet-topup-payment-method"/>
      <div ref={googleRef} className="wallet-topup-payment-method wallet-topup-google-pay"/>
      <div ref={appleRef} className="wallet-topup-payment-method wallet-topup-apple-pay"/>
      {processing ? <small className="text-muted d-block mt-2">Pagamento in conferma...</small> : null}
    </div>
  );
};

WalletTopUpPaymentMethods.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  token: PropTypes.string.isRequired,
  isValidAmount: PropTypes.func.isRequired,
  onInvalidAmount: PropTypes.func.isRequired,
  onPending: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
};

WalletTopUpPaymentMethods.defaultProps = {
  amount: DEFAULT_AMOUNT
};

export default WalletTopUpPaymentMethods;
